import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { ChevronRight, Menu, Moon, PanelLeftClose, PanelLeftOpen, Sun } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { availableNavigation, currentNavigation, type NavigationItem } from '@/config/navigation';
import {
  NavigationAccount,
  NavigationBrand,
  NavigationLinks,
  NavigationSearch,
  StudioMobileNavigation,
} from '@/components/navigation/StudioNavigation';
import PWAInstallPrompt from '@/components/common/PWAInstallPrompt';
import MutationLoadingOverlay from '@/components/common/MutationLoadingOverlay';
import NotificationPopover from '@/components/notifications/NotificationPopover';
import UserGuide from '@/components/help/UserGuide';
import WhatsNew from '@/components/releases/WhatsNew';
import { cn } from '@/lib/utils';
import '@/styles/studio-navigation.css';
import '@/styles/project-theme.css';

function savedPreference(key: string) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function savePreference(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* In-memory state remains usable. */
  }
}
function useTheme() {
  const [isDark, setIsDark] = useState(() => {
    const dark = savedPreference('klothiq-theme') !== 'light';
    document.documentElement.classList.toggle('dark', dark);
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', dark ? '#000000' : '#f3f5f9');
    return dark;
  });
  return {
    isDark,
    toggle: () =>
      setIsDark((previous) => {
        const next = !previous;
        document.documentElement.classList.toggle('dark', next);
        document
          .querySelector('meta[name="theme-color"]')
          ?.setAttribute('content', next ? '#000000' : '#f3f5f9');
        savePreference('klothiq-theme', next ? 'dark' : 'light');
        return next;
      }),
  };
}
function getBreadcrumb(pathname: string, items: readonly NavigationItem[]) {
  const match = currentNavigation(pathname, items);
  if (!match) return { label: 'KLOTHIQ' };
  const sub = pathname.replace(match.path, '').replace(/^\//, '');
  if (sub === 'new') return { label: 'New', parent: match.label };
  if (sub.includes('edit')) return { label: 'Edit', parent: match.label };
  return { label: match.label };
}

export default function AppLayout() {
  const { profile, organization, access, signOut, role, can } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(
    () => savedPreference('klothiq-sidebar') === 'collapsed',
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktop, setDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  const [query, setQuery] = useState('');
  const [mobileQuery, setMobileQuery] = useState('');
  const { isDark, toggle: toggleTheme } = useTheme();
  const location = useLocation();
  const productEditor = /^\/products\/(?:new|[^/]+\/edit)\/?$/.test(location.pathname);
  const items = availableNavigation(role, can);
  const breadcrumb = getBreadcrumb(location.pathname, items);
  const name = profile?.full_name || 'User';
  const scope = [organization?.id, profile?.id, role, access?.access_version, access?.ready].join(
    ':',
  );

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const resize = () => {
      setDesktop(media.matches);
      if (media.matches) setMobileOpen(false);
    };
    media.addEventListener('change', resize);
    return () => media.removeEventListener('change', resize);
  }, []);
  useEffect(() => {
    setMobileOpen(false);
    setMobileQuery('');
    setQuery('');
  }, [location.pathname, location.search]);
  useEffect(() => {
    setMobileOpen(false);
    setMobileQuery('');
    setQuery('');
  }, [scope]);

  function toggleCollapsed() {
    setIsCollapsed((previous) => {
      savePreference('klothiq-sidebar', previous ? 'expanded' : 'collapsed');
      return !previous;
    });
  }
  function changeQuery(value: string) {
    setQuery(value);
    if (value.trim()) setIsCollapsed(false); // Search opens labels without changing the saved pin.
  }
  function changeMobileOpen(next: boolean) {
    setMobileOpen(next);
    if (!next) setMobileQuery('');
  }
  async function handleSignOut() {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  }

  return (
    <Dialog.Root open={mobileOpen && !desktop} onOpenChange={changeMobileOpen}>
      <div
        className={cn(
          'app-shell studio-shell flex h-full min-h-0 overflow-hidden',
          isCollapsed && 'studio-compact',
        )}
      >
        <aside
          className={cn('studio-sidebar', isCollapsed && 'is-compact')}
          aria-label="Desktop navigation"
        >
          <NavigationBrand />
          <NavigationLinks
            key={scope}
            items={items}
            compact={isCollapsed}
            query={query}
            onClear={() => setQuery('')}
          />
          <NavigationAccount
            items={items}
            name={name}
            role={role}
            compact={isCollapsed}
            onSignOut={() => void handleSignOut()}
          />
        </aside>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <header className="studio-header">
            <button
              type="button"
              className="studio-icon-button studio-desktop-toggle"
              onClick={toggleCollapsed}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!isCollapsed}
            >
              {isCollapsed ? (
                <PanelLeftOpen aria-hidden="true" />
              ) : (
                <PanelLeftClose aria-hidden="true" />
              )}
            </button>
            <Dialog.Trigger asChild>
              <button
                type="button"
                className="studio-icon-button studio-mobile-opener"
                aria-label="Open menu"
              >
                <Menu aria-hidden="true" />
              </button>
            </Dialog.Trigger>
            <div className="studio-breadcrumb">
              {breadcrumb.parent && (
                <>
                  <span className="studio-breadcrumb-parent">{breadcrumb.parent}</span>
                  <ChevronRight aria-hidden="true" />
                </>
              )}
              <strong
                title={
                  breadcrumb.parent
                    ? `${breadcrumb.parent} / ${breadcrumb.label}`
                    : breadcrumb.label
                }
              >
                {breadcrumb.label}
              </strong>
            </div>
            <div className="studio-header-search">
              <NavigationSearch value={query} onChange={changeQuery} />
            </div>
            <div className="studio-header-actions">
              <UserGuide compact />
              <WhatsNew />
              <NotificationPopover />
              <button
                type="button"
                className="studio-icon-button"
                onClick={toggleTheme}
                aria-label={isDark ? 'Light mode' : 'Dark mode'}
              >
                {isDark ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
              </button>
              <span className="studio-avatar studio-header-avatar" title={name}>
                {name.charAt(0).toUpperCase()}
              </span>
            </div>
          </header>
          <main
            className={cn(
              'studio-main min-h-0 flex-1',
              productEditor ? 'flex flex-col overflow-hidden' : 'overflow-y-auto',
            )}
          >
            <div
              className={cn(
                'p-4 lg:p-5',
                productEditor ? 'flex min-h-0 flex-1 flex-col overflow-hidden' : 'page-enter',
              )}
            >
              <Outlet />
            </div>
          </main>
          <PWAInstallPrompt />
          <MutationLoadingOverlay />
        </div>
      </div>
      {!desktop && (
        <StudioMobileNavigation
          items={items}
          query={mobileQuery}
          onQueryChange={setMobileQuery}
          onClose={() => changeMobileOpen(false)}
          name={name}
          role={role}
          onSignOut={() => void handleSignOut()}
          scope={scope}
        />
      )}
    </Dialog.Root>
  );
}
